import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import siteAssets from "@/lib/siteAssets";

const HeroSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.35], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 60]);

  return (
    <section
      ref={ref}
      className="relative h-[100svh] overflow-hidden flex items-center"
      style={{
        backgroundImage: `url("${siteAssets.heroImage}")`,
        backgroundSize: "cover",
        backgroundPosition: "center right",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div
        className="absolute inset-0 z-[1]"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.50)" }}
      />
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14"
      >
        <div className="max-w-[620px] mx-auto">
          <h1
            className="text-[clamp(2.2rem,4.8vw,3.5rem)] font-bold leading-[1.08] tracking-tight text-white text-balance text-center"
            style={{ textShadow: "none" }}
          >
            Smidigare service.
            <br />
            <span style={{ color: "#026133" }}>Bättre</span> flöde.
          </h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-3 mt-8"
          >
            <a href="#book">
              <Button
                size="lg"
                style={{ backgroundColor: "#026133", borderRadius: "12px" }}
                className="cta-glow px-6 h-[44px] text-sm font-medium text-white hover:brightness-110 border-0"
              >
                Boka demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <a href="#faq">
              <Button
                size="lg"
                variant="outline"
                style={{ borderRadius: "12px", borderWidth: "2px" }}
                className="px-6 h-[44px] text-sm font-medium bg-transparent text-white border-white hover:brightness-110"
              >
                <Play className="mr-2 h-3.5 w-3.5 fill-current" />
                Se hur det fungerar
              </Button>
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats row */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="absolute bottom-8 md:bottom-10 inset-x-0 z-10 flex justify-center"
      >
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm md:text-base text-white whitespace-nowrap">
          <a href="#features" className="hover:underline" style={{ color: "#FFFFFF" }}>
            Byggt för restauranger
          </a>
          <span aria-hidden className="opacity-70">·</span>
          <a href="#how-it-works" className="hover:underline" style={{ color: "#FFFFFF" }}>
            Livesynk i realtid
          </a>
          <span aria-hidden className="opacity-70">·</span>
          <a href="/kontakt" className="hover:underline" style={{ color: "#FFFFFF" }}>
            Kom igång idag
          </a>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
